import * as React from "react";
import { ChakraProvider } from "@chakra-ui/react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./redux/store";
import theme from "theme";
// Fonts
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/ibm-plex-mono/400.css";
// Providers
import EmotionCacheProvider from "components/EmotionCacheProvider";
// Custom Routes
import ProtectedRoute from "components/ProtectedRoute";
import ProtectedRouteBlank from "components/ProtectedRoute/ProtectedRouteBlank";
// Layouts
import GuestLayout from "layouts/GuestLayout";
// Guest Pages
import Login from "pages/Login";
import ConfirmAccount from "pages/ConfirmAccount";
import ActivateAccount from "pages/ActivateAccount";
import SetPasswordAccount from "pages/SetPasswordAccount";
import OAuth2 from "pages/OAuth2";
import APICredentials from "pages/APICredentials";
import ReconcilationSummary from "pages/ReconcilationSummary";
import TransactionHistoryView from "pages/TransactionHistoryView";
// Auth Pages

const Users = React.lazy(() => import("pages/Users"));
const PendingUsers = React.lazy(() => import("pages/PendingUsers"));
const ManageRoles = React.lazy(() => import("pages/ManageRoles"));
const TPPPListView = React.lazy(() => import("pages/TpppList"));
const TPPPDetailView = React.lazy(() => import("pages/TPPPDetailView"));
const TPPPMerchantsView = React.lazy(() => import("pages/TPPPMerchantsView"));
const TPPPCreateMerchant = React.lazy(
  () => import("pages/TPPPMerchantsView/TPPPCreateMerchants/TPPPCreateMechants")
);
const UploadMerchant = React.lazy(() => import("pages/UploadMerchant"));
const MerchantDetailView = React.lazy(() => import("pages/MerchantDetailView"));
const PaymentHistoryView = React.lazy(() => import("pages/PaymentHistoryView"));
const PaymentDetailView = React.lazy(() => import("pages/PaymentDetailView"));
const DocumentationView = React.lazy(() => import("pages/Documentation"));
const Logout = React.lazy(() => import("pages/OAuth2/Logout"));
const Reconciliation = React.lazy(() => import("pages/Reconciliation"));
const UploadTransction = React.lazy(() => import("pages/UploadTransaction"));
const Home = React.lazy(() => import("pages/Home"));

const ProfileView = React.lazy(() => import("pages/ProfileView"));
const MerchantCategoryCodeView = React.lazy(
  () => import("pages/MerchantCategoryCode")
);
const BusinessActivityView = React.lazy(() => import("pages/BusinessActivity"));
const UserActivityView = React.lazy(
  () => import("pages/BusinessActivity/UserActivity")
);
const UserRequestsView = React.lazy(
  () => import("pages/BusinessActivity/UserRequests")
);
const UserRequestDetailsView = React.lazy(
  () => import("pages/BusinessActivity/UserRequestDetails")
);

const BusinessActivityRequestDetails = React.lazy(
  () => import("pages/BusinessActivity/RequestDetails")
);
const BusinessActivityRequests = React.lazy(
  () => import("pages/BusinessActivity/ActivityRequests/")
);

const appGuestRoutes = [
  {
    path: "/",
    component: <Login />,
  },
  {
    path: "/set-password",
    component: <SetPasswordAccount />,
  },
  {
    path: "/confirm-account",
    component: <ConfirmAccount />,
  },
  {
    path: "/auth/activate",
    component: <ActivateAccount />,
  },

  {
    path: "/auth/authorize",
    component: <OAuth2 />,
  },
  // {
  //   path: "/register",
  //   component: <Register />,
  // },
  // {
  //   path: "/register-success",
  //   component: <RegisterSuccess />,
  // },
];

const appProtectedRoutes = [
  {
    path: "/home",
    component: <Home />,
  },
  {
    path: "/logout",
    component: <Logout />,
  },
  {
    path: "/business-activity",
    component: <BusinessActivityView />,
  },
  {
    path: "/reconciliation",
    component: <Reconciliation />,
  },
  {   
    path: "/upload-transaction",
    component: <UploadTransction />,
  },
  {   
    path: "/view-reconciliation-summary/:IdReconciliate",
    component: <ReconcilationSummary />,
  },
   {   
    path: "/view-transaction-history",
    component: <TransactionHistoryView />,
  },
  {
    path: "/user-activity/:userId/:executedDate",
    component: <UserRequestsView />,
  },
  {
    path: "/user-activity",
    component: <UserActivityView />,
  },

  {
    path: "/business-activity/:batchRequestId",
    component: <BusinessActivityRequests />,
  },
  {
    path: "/business-activity-requests/:requestId",
    component: <BusinessActivityRequestDetails />,
  },
  {
    path: "/user-activity-requests/:requestId",
    component: <UserRequestDetailsView />,
  },
  {
    path: "/active-tppp-users",
    component: <Users />,
  },
  {
    path: "/active-collaborators",
    component: <Users />,
  },
  {
    path: "/activation-pending-users",
    component: <PendingUsers />,
  },
  {
    path: "/manage-roles",
    component: <ManageRoles />,
  },
  {
    path: "/api-credentials",
    component: <APICredentials />,
  },
  {
    path: "/tppp-list-view",
    component: <TPPPListView />,
  },
  {
    path: "/tppp-detail-view/:orgId",
    component: <TPPPDetailView />,
  },
  {
    path: "/tppp-merchants-view",
    component: <TPPPMerchantsView />,
  },
  {
    path: "/tppp-merchants-view/create-merchant",
    component: <TPPPCreateMerchant />,
  },
  {
    path: "/tppp-merchants-category-code-view",
    component: <MerchantCategoryCodeView />,
  },
  {
    path: "/upload-merchant",
    component: <UploadMerchant />,
  },
  {
    path: "/merchant-detail/:merchantId",
    component: <MerchantDetailView />,
  },
  {
    path: "/merchant-detail/:merchantId/user/:userId",
    component: <MerchantDetailView />,
  },
  {
    path: "/payment-history-view",
    component: <PaymentHistoryView />,
  },
  {
    path: "/payment-detail/:paymentId",
    component: <PaymentDetailView />,
  },
  {
    path: "/myAccount",
    component: <ProfileView />,
  },
];

export const App = () => (
  <EmotionCacheProvider>
    <ChakraProvider theme={theme}>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <Router>
            {appGuestRoutes.map((n) => (
              <Route key={n.path} exact path={n.path}>
                <GuestLayout>{n.component}</GuestLayout>
              </Route>
            ))}
            <Route exact path="/documentation">
              <ProtectedRouteBlank>
                <DocumentationView />
              </ProtectedRouteBlank>
            </Route>
            {appProtectedRoutes.map((n) => (
              <ProtectedRoute key={n.path} exact path={n.path}>
                {n.component}
              </ProtectedRoute>
            ))}
          </Router>
        </PersistGate>
      </Provider>
    </ChakraProvider>
  </EmotionCacheProvider>
);
